import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvGet, dvCreate, dvUpdate, dvDelete } from '../api/dataverseClient';
import { getDataverseHeaders } from '../auth/authService';
import { getDataverseApiBase } from './environmentService';


// ── Picklists (verified via EntityDefinitions on prod) ────────────────
export const TargetEntityType = {
  Task: 100000000, Issue: 100000001, ChangeRequest: 100000002, ProjectRequest: 100000003, BugReport: 100000004,
} as const;
export const TARGET_LABEL: Record<number, string> = {
  [TargetEntityType.Task]: 'Task', [TargetEntityType.Issue]: 'Issue',
  [TargetEntityType.ChangeRequest]: 'Change Request', [TargetEntityType.ProjectRequest]: 'Project Request',
  [TargetEntityType.BugReport]: 'Bug Report',
};

export const FieldType = {
  Text: 100000000, LongText: 100000001, Number: 100000002, Date: 100000003,
  FileAttachment: 100000004, ChoiceSingle: 100000005, Person: 100000006, Email: 100000007,
} as const;
export const FIELD_TYPE_LABEL: Record<number, string> = {
  [FieldType.Text]: 'Text', [FieldType.LongText]: 'Long Text', [FieldType.Number]: 'Number', [FieldType.Date]: 'Date',
  [FieldType.FileAttachment]: 'File', [FieldType.ChoiceSingle]: 'Dropdown', [FieldType.Person]: 'Person', [FieldType.Email]: 'Email',
};

export const SubmissionStatus = {
  Pending: 100000000, InReview: 100000001, Approved: 100000002, Rejected: 100000003, Complete: 100000004,
} as const;
export const SUBMISSION_STATUS_LABEL: Record<number, string> = {
  [SubmissionStatus.Pending]: 'Pending', [SubmissionStatus.InReview]: 'In Review',
  [SubmissionStatus.Approved]: 'Approved', [SubmissionStatus.Rejected]: 'Rejected', [SubmissionStatus.Complete]: 'Complete',
};

// ── Types ─────────────────────────────────────────────────────────────
export interface FormDef {
  dagrun_pm_formid: string;
  dagrun_pm_name: string;
  dagrun_pm_formdescription?: string | null;
  dagrun_pm_ispublic?: boolean;
  dagrun_pm_isactive?: boolean;
  dagrun_pm_targetentitytype?: number | null;
  dagrun_pm_anonymousratelimitperhour?: number | null;
  dagrun_pm_sendconfirmationemail?: boolean;
  dagrun_pm_attachmentallowedtypes?: string | null;
  dagrun_pm_attachmentmaxmb?: number | null;
  dagrun_pm_requiresreview?: boolean;
  _dagrun_pm_projectid_value?: string | null;
}
export interface FormField {
  dagrun_pm_formfieldid: string;
  dagrun_pm_name: string;
  dagrun_pm_fieldtype: number;
  dagrun_pm_isrequired?: boolean;
  dagrun_pm_choiceoptionsjson?: string | null;
  dagrun_pm_placeholdertext?: string | null;
  dagrun_pm_mapstofield?: string | null;
  dagrun_pm_sortorder?: number | null;
  _dagrun_pm_formid_value?: string | null;
}
export interface Submission {
  dagrun_pm_submissionid: string;
  dagrun_pm_name?: string | null;
  dagrun_pm_submissiondatajson?: string | null;
  dagrun_pm_submitteremail?: string | null;
  dagrun_pm_submittername?: string | null;
  dagrun_pm_status?: number | null;
  dagrun_pm_submittedon?: string | null;
  dagrun_pm_reviewernotes?: string | null;
  dagrun_pm_reviewedby?: string | null;
  dagrun_pm_reviewedbyname?: string | null;
  dagrun_pm_reviewedon?: string | null;
  dagrun_pm_createdentitytype?: string | null;
  dagrun_pm_createdentityid?: string | null;
  _dagrun_pm_formid_value?: string | null;
  '@odata.etag'?: string;
}

// A submission is "resolved" (read-only) once any approver has acted.
export function isSubmissionResolved(s: Submission): boolean {
  return s.dagrun_pm_status === SubmissionStatus.Approved
    || s.dagrun_pm_status === SubmissionStatus.Rejected
    || s.dagrun_pm_status === SubmissionStatus.Complete;
}

export interface FormApprover {
  dagrun_pm_formapproverid: string;
  dagrun_pm_name: string;
  dagrun_pm_userid: string | null;
  dagrun_pm_username: string | null;
  dagrun_pm_useremail: string | null;
  dagrun_pm_addedby?: string | null;
  dagrun_pm_addedbyname?: string | null;
  _dagrun_pm_formid_value?: string | null;
}

const FORM_FIELDS = [
  'dagrun_pm_formid', 'dagrun_pm_name', 'dagrun_pm_formdescription', 'dagrun_pm_ispublic', 'dagrun_pm_isactive',
  'dagrun_pm_targetentitytype', 'dagrun_pm_anonymousratelimitperhour', 'dagrun_pm_sendconfirmationemail',
  'dagrun_pm_attachmentallowedtypes', 'dagrun_pm_attachmentmaxmb', 'dagrun_pm_requiresreview', '_dagrun_pm_projectid_value',
];
const FIELD_FIELDS = [
  'dagrun_pm_formfieldid', 'dagrun_pm_name', 'dagrun_pm_fieldtype', 'dagrun_pm_isrequired',
  'dagrun_pm_choiceoptionsjson', 'dagrun_pm_placeholdertext', 'dagrun_pm_mapstofield', 'dagrun_pm_sortorder', '_dagrun_pm_formid_value',
];
const SUB_FIELDS = [
  'dagrun_pm_submissionid', 'dagrun_pm_name', 'dagrun_pm_submissiondatajson', 'dagrun_pm_submitteremail',
  'dagrun_pm_submittername', 'dagrun_pm_status', 'dagrun_pm_submittedon', '_dagrun_pm_formid_value',
  'dagrun_pm_reviewernotes', 'dagrun_pm_reviewedby', 'dagrun_pm_reviewedbyname', 'dagrun_pm_reviewedon',
  'dagrun_pm_createdentitytype', 'dagrun_pm_createdentityid',
];

// ── Forms ─────────────────────────────────────────────────────────────
export async function getProjectForms(pca: IPublicClientApplication, projectId: string): Promise<FormDef[]> {
  return dvList<FormDef>(pca, 'dagrun_pm_forms', {
    select: FORM_FIELDS, filter: `_dagrun_pm_projectid_value eq ${projectId}`, orderby: 'createdon desc', top: 100,
  });
}
export async function getForm(pca: IPublicClientApplication, formId: string): Promise<FormDef> {
  return dvGet<FormDef>(pca, 'dagrun_pm_forms', formId, { select: FORM_FIELDS });
}

export interface FormSettingsInput {
  name: string; description?: string | null; isPublic: boolean; isActive: boolean;
  targetEntityType: number; sendConfirmationEmail: boolean; ratePerHour?: number | null;
  attachmentTypes?: string | null; attachmentMaxMb?: number | null; requiresReview: boolean;
}
function formBody(s: FormSettingsInput): Record<string, unknown> {
  return {
    dagrun_pm_name: s.name,
    dagrun_pm_formdescription: s.description ?? null,
    dagrun_pm_ispublic: s.isPublic,
    dagrun_pm_isactive: s.isActive,
    dagrun_pm_targetentitytype: s.targetEntityType,
    dagrun_pm_sendconfirmationemail: s.sendConfirmationEmail,
    dagrun_pm_anonymousratelimitperhour: s.ratePerHour ?? null,
    dagrun_pm_attachmentallowedtypes: s.attachmentTypes ?? null,
    dagrun_pm_attachmentmaxmb: s.attachmentMaxMb ?? null,
    dagrun_pm_requiresreview: s.requiresReview,
  };
}
export async function createForm(pca: IPublicClientApplication, projectId: string, s: FormSettingsInput): Promise<string> {
  return dvCreate(pca, 'dagrun_pm_forms', { ...formBody(s), 'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${projectId})` });
}
export async function updateForm(pca: IPublicClientApplication, formId: string, s: FormSettingsInput): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_forms', formId, formBody(s));
}
export async function deleteForm(pca: IPublicClientApplication, formId: string): Promise<void> {
  return dvDelete(pca, 'dagrun_pm_forms', formId);
}

// ── Form fields ───────────────────────────────────────────────────────
export async function getFormFields(pca: IPublicClientApplication, formId: string): Promise<FormField[]> {
  return dvList<FormField>(pca, 'dagrun_pm_formfields', {
    select: FIELD_FIELDS, filter: `_dagrun_pm_formid_value eq ${formId}`, orderby: 'dagrun_pm_sortorder asc', top: 100,
  });
}
export interface FieldInput {
  label: string; fieldType: number; required: boolean; sortOrder: number;
  choiceOptions?: string | null; placeholder?: string | null; mapsToField?: string | null;
}
function fieldBody(f: FieldInput): Record<string, unknown> {
  return {
    dagrun_pm_name: f.label,
    dagrun_pm_fieldtype: f.fieldType,
    dagrun_pm_isrequired: f.required,
    dagrun_pm_sortorder: f.sortOrder,
    dagrun_pm_choiceoptionsjson: f.choiceOptions ?? null,
    dagrun_pm_placeholdertext: f.placeholder ?? null,
    dagrun_pm_mapstofield: f.mapsToField ?? null,
  };
}
export async function createFormField(pca: IPublicClientApplication, formId: string, f: FieldInput): Promise<string> {
  return dvCreate(pca, 'dagrun_pm_formfields', {
    ...fieldBody(f),
    'dagrun_pm_formid@odata.bind': `/dagrun_pm_forms(${formId})`,
  });
}
export async function updateFormField(pca: IPublicClientApplication, fieldId: string, f: FieldInput): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_formfields', fieldId, fieldBody(f));
}

// ── Entity-driven field discovery (form builder) ──────────────────────
// Forms submit into Dataverse entities, so fields map to real entity
// attributes. We read the target entity's metadata and derive a form
// field type from each attribute's AttributeType.

export const ATTACHMENT_TYPES = 'pdf,docx,doc,xlsx,xls,pptx,ppt,png,jpg,jpeg,gif,txt,csv,mp4,mov';

export function targetEntityLogicalName(targetType: number): string {
  switch (targetType) {
    case TargetEntityType.Issue: return 'dagrun_pm_issue';
    case TargetEntityType.ChangeRequest: return 'dagrun_pm_changerequest';
    // Task / ProjectRequest / BugReport are all task-shaped intake.
    default: return 'dagrun_pm_task';
  }
}

export interface EntityFieldMeta {
  logicalName: string;
  displayName: string;
  fieldType: number;            // mapped FieldType value
  attributeType: string;        // raw Dataverse AttributeType
}

const TYPE_MAP: Record<string, number> = {
  String: FieldType.Text, Memo: FieldType.LongText, DateTime: FieldType.Date,
  Picklist: FieldType.ChoiceSingle, State: FieldType.ChoiceSingle, Status: FieldType.ChoiceSingle,
  Integer: FieldType.Number, BigInt: FieldType.Number, Decimal: FieldType.Number,
  Double: FieldType.Number, Money: FieldType.Number, Boolean: FieldType.ChoiceSingle,
};

async function metaGet<T>(pca: IPublicClientApplication, path: string): Promise<T> {
  const headers = await getDataverseHeaders(pca);
  const res = await fetch(`${getDataverseApiBase()}${path}`, { headers });
  if (!res.ok) throw new Error(`metadata ${path} failed: ${res.status}`);
  return res.json() as Promise<T>;
}

/** Fillable dagrun_pm_ fields on the target entity, mapped to form field types. */
export async function getEntityFields(pca: IPublicClientApplication, entityLogicalName: string): Promise<EntityFieldMeta[]> {
  // startswith() is unsupported on metadata, so fetch all + filter client-side.
  const data = await metaGet<{ value: Array<{ LogicalName: string; AttributeType: string; DisplayName?: { UserLocalizedLabel?: { Label?: string } } }> }>(
    pca, `/EntityDefinitions(LogicalName='${entityLogicalName}')/Attributes?$select=LogicalName,AttributeType,DisplayName`,
  );
  const out: EntityFieldMeta[] = [];
  for (const a of data.value) {
    if (!a.LogicalName.startsWith('dagrun_pm_')) continue;
    if (a.LogicalName.endsWith('idname')) continue; // denormalized lookup names
    if (a.LogicalName === `${entityLogicalName}id`) continue; // primary key
    const mapped = TYPE_MAP[a.AttributeType];
    if (mapped == null) continue; // skip lookups / virtual / etc.
    const label = a.DisplayName?.UserLocalizedLabel?.Label;
    if (!label) continue;
    out.push({ logicalName: a.LogicalName, displayName: label, fieldType: mapped, attributeType: a.AttributeType });
  }
  out.sort((x, y) => x.displayName.localeCompare(y.displayName));
  return out;
}

/** Option-set labels for a picklist attribute (for ChoiceSingle fields). Boolean → Yes/No. */
export async function getEntityFieldOptions(pca: IPublicClientApplication, entityLogicalName: string, attributeLogicalName: string, attributeType: string): Promise<string[]> {
  if (attributeType === 'Boolean') return ['Yes', 'No'];
  try {
    const data = await metaGet<{ OptionSet?: { Options?: Array<{ Label?: { UserLocalizedLabel?: { Label?: string } } }> } }>(
      pca, `/EntityDefinitions(LogicalName='${entityLogicalName}')/Attributes(LogicalName='${attributeLogicalName}')/Microsoft.Dynamics.CRM.PicklistAttributeMetadata?$select=LogicalName&$expand=OptionSet($select=Options)`,
    );
    return (data.OptionSet?.Options ?? []).map(o => o.Label?.UserLocalizedLabel?.Label ?? '').filter(Boolean);
  } catch {
    return [];
  }
}
export async function deleteFormField(pca: IPublicClientApplication, fieldId: string): Promise<void> {
  return dvDelete(pca, 'dagrun_pm_formfields', fieldId);
}

// ── Submissions ───────────────────────────────────────────────────────
export async function getFormSubmissions(pca: IPublicClientApplication, formId: string): Promise<Submission[]> {
  return dvList<Submission>(pca, 'dagrun_pm_submissions', {
    select: SUB_FIELDS, filter: `_dagrun_pm_formid_value eq ${formId}`, orderby: 'dagrun_pm_submittedon desc', top: 200,
  });
}
export async function getRecentSubmissionCount(pca: IPublicClientApplication, formId: string, sinceIso: string): Promise<number> {
  const rows = await dvList<{ dagrun_pm_submissionid: string }>(pca, 'dagrun_pm_submissions', {
    select: ['dagrun_pm_submissionid'],
    filter: `_dagrun_pm_formid_value eq ${formId} and dagrun_pm_submittedon ge ${sinceIso}`,
    top: 200,
  });
  return rows.length;
}
export interface SubmissionInput {
  formId: string; projectId?: string | null; data: Record<string, unknown>;
  submitterName?: string | null; submitterEmail?: string | null;
}
export async function createSubmission(pca: IPublicClientApplication, s: SubmissionInput): Promise<string> {
  const body: Record<string, unknown> = {
    dagrun_pm_name: `Submission — ${new Date().toLocaleString()}`.slice(0, 100),
    dagrun_pm_submissiondatajson: JSON.stringify(s.data),
    dagrun_pm_submittername: s.submitterName ?? null,
    dagrun_pm_submitteremail: s.submitterEmail ?? null,
    dagrun_pm_status: SubmissionStatus.Pending,
    dagrun_pm_submittedon: new Date().toISOString(),
    'dagrun_pm_formid@odata.bind': `/dagrun_pm_forms(${s.formId})`,
  };
  if (s.projectId) body['dagrun_pm_projectid@odata.bind'] = `/dagrun_pm_projects(${s.projectId})`;
  return dvCreate(pca, 'dagrun_pm_submissions', body);
}

export async function markSubmissionConverted(
  pca: IPublicClientApplication, submissionId: string, entityType: string, entityId: string,
): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_submissions', submissionId, {
    dagrun_pm_status: SubmissionStatus.Complete,
    dagrun_pm_createdentitytype: entityType,
    dagrun_pm_createdentityid: entityId,
  });
}

export async function getFormSubmissionCount(pca: IPublicClientApplication, formId: string): Promise<number> {
  return getRecentSubmissionCount(pca, formId, '1970-01-01T00:00:00Z');
}

export async function getSubmission(pca: IPublicClientApplication, id: string): Promise<Submission> {
  return dvGet<Submission>(pca, 'dagrun_pm_submissions', id, { select: SUB_FIELDS });
}

// ── Form approvers ─────────────────────────────────────────────────────
const APPROVER_FIELDS = [
  'dagrun_pm_formapproverid', 'dagrun_pm_name', 'dagrun_pm_userid', 'dagrun_pm_username',
  'dagrun_pm_useremail', 'dagrun_pm_addedby', 'dagrun_pm_addedbyname', '_dagrun_pm_formid_value',
];
export async function getFormApprovers(pca: IPublicClientApplication, formId: string): Promise<FormApprover[]> {
  return dvList<FormApprover>(pca, 'dagrun_pm_formapprovers', {
    select: APPROVER_FIELDS, filter: `_dagrun_pm_formid_value eq ${formId}`, orderby: 'dagrun_pm_username asc', top: 100,
  });
}
export async function addFormApprover(pca: IPublicClientApplication, input: {
  formId: string; userId: string; userName: string; userEmail?: string | null; addedBy: string; addedByName: string;
}): Promise<string> {
  return dvCreate(pca, 'dagrun_pm_formapprovers', {
    dagrun_pm_name: input.userName.slice(0, 300),
    dagrun_pm_userid: input.userId,
    dagrun_pm_username: input.userName,
    dagrun_pm_useremail: input.userEmail ?? null,
    dagrun_pm_addedby: input.addedBy,
    dagrun_pm_addedbyname: input.addedByName,
    'dagrun_pm_FormId@odata.bind': `/dagrun_pm_forms(${input.formId})`,
  });
}
export async function removeFormApprover(pca: IPublicClientApplication, id: string): Promise<void> {
  return dvDelete(pca, 'dagrun_pm_formapprovers', id);
}

// ── Target-entity creation from a submission (field mappings) ──────────
async function getPicklistValueMap(pca: IPublicClientApplication, entity: string, attr: string): Promise<Map<string, number>> {
  try {
    const data = await metaGet<{ OptionSet?: { Options?: Array<{ Value?: number; Label?: { UserLocalizedLabel?: { Label?: string } } }> } }>(
      pca, `/EntityDefinitions(LogicalName='${entity}')/Attributes(LogicalName='${attr}')/Microsoft.Dynamics.CRM.PicklistAttributeMetadata?$select=LogicalName&$expand=OptionSet($select=Options)`,
    );
    const m = new Map<string, number>();
    (data.OptionSet?.Options ?? []).forEach(o => {
      const label = o.Label?.UserLocalizedLabel?.Label;
      if (label != null && o.Value != null) m.set(label.toLowerCase(), o.Value);
    });
    return m;
  } catch { return new Map(); }
}

async function coerceForAttr(pca: IPublicClientApplication, entity: string, attr: string, attrType: string | undefined, raw: unknown): Promise<unknown | undefined> {
  const s = String(raw).trim();
  if (s === '') return undefined;
  switch (attrType) {
    case 'Integer': case 'BigInt': { const n = parseInt(s.replace(/[^\d-]/g, ''), 10); return isFinite(n) ? n : undefined; }
    case 'Decimal': case 'Double': case 'Money': { const n = parseFloat(s.replace(/[^\d.-]/g, '')); return isFinite(n) ? n : undefined; }
    case 'DateTime': return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : (isNaN(Date.parse(s)) ? undefined : new Date(s).toISOString());
    case 'Boolean': return /^(yes|true|1)$/i.test(s);
    case 'Picklist': { const map = await getPicklistValueMap(pca, entity, attr); return map.get(s.toLowerCase()); }
    case 'State': case 'Status': return undefined; // system-managed — never set from a form
    case 'String': case 'Memo': default: return s;
  }
}

/**
 * Build the target entity's create body from a submission's answers via each
 * form field's maps_to_field, coercing each value to the real attribute type.
 * Guarantees a primary name and binds the project.
 */
export async function createTargetEntityFromSubmission(
  pca: IPublicClientApplication,
  form: FormDef,
  fields: FormField[],
  data: Record<string, unknown>,
  projectId: string,
): Promise<{ entityType: string; entityId: string }> {
  const logical = targetEntityLogicalName(form.dagrun_pm_targetentitytype ?? TargetEntityType.Task);
  const entitySet = `${logical}s`;
  const metas = await getEntityFields(pca, logical).catch(() => [] as EntityFieldMeta[]);
  const typeByLogical = new Map(metas.map(m => [m.logicalName, m.attributeType]));

  const body: Record<string, unknown> = {
    'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${projectId})`,
  };
  let nameSet = false;
  for (const f of fields) {
    const target = f.dagrun_pm_mapstofield;
    if (!target) continue;
    const raw = data[f.dagrun_pm_name];
    if (raw == null || raw === '') continue;
    const coerced = await coerceForAttr(pca, logical, target, typeByLogical.get(target), raw);
    if (coerced === undefined) continue;
    body[target] = coerced;
    if (target === 'dagrun_pm_name') nameSet = true;
  }
  if (!nameSet) {
    const firstText = fields.map(f => data[f.dagrun_pm_name]).find(v => typeof v === 'string' && v.trim());
    body.dagrun_pm_name = String(firstText ?? form.dagrun_pm_name ?? 'Form submission').slice(0, 300);
  }
  const entityId = await dvCreate(pca, entitySet, body);
  return { entityType: logical, entityId };
}

// ── Review / approval (parallel, first-action-wins, ETag concurrency) ──
export type ReviewOutcome = 'approved' | 'rejected' | 'conflict';

// Optimistic-concurrency PATCH. If-Match on the row's ETag → 412 when
// another approver has already acted, so two approvals can't both proceed.
async function patchSubmissionIfMatch(
  pca: IPublicClientApplication, id: string, etag: string, body: Record<string, unknown>,
): Promise<'applied' | 'conflict'> {
  const headers = { ...(await getDataverseHeaders(pca)), 'If-Match': etag } as Record<string, string>;
  const res = await fetch(`${getDataverseApiBase()}/dagrun_pm_submissions(${id})`, { method: 'PATCH', headers, body: JSON.stringify(body) });
  if (res.status === 412 || res.status === 404) return 'conflict';
  if (!res.ok) throw new Error(`submission patch ${res.status}: ${(await res.text().catch(() => '')).slice(0, 200)}`);
  return 'applied';
}

/**
 * Approve a pending submission: atomically claim it (If-Match), create the
 * target entity via the field mappings, then mark Complete. Returns
 * 'conflict' when another approver already resolved it. On target-create
 * failure the claim is rolled back so another approver can retry.
 */
export async function approveSubmission(pca: IPublicClientApplication, params: {
  submission: Submission; form: FormDef; fields: FormField[]; projectId: string;
  reviewerId: string; reviewerName: string;
}): Promise<ReviewOutcome> {
  const fresh = await getSubmission(pca, params.submission.dagrun_pm_submissionid);
  if (isSubmissionResolved(fresh) || fresh.dagrun_pm_status === SubmissionStatus.InReview) return 'conflict';
  const etag = fresh['@odata.etag'];
  if (!etag) return 'conflict';
  const nowIso = new Date().toISOString();
  const claim = await patchSubmissionIfMatch(pca, fresh.dagrun_pm_submissionid, etag, {
    dagrun_pm_status: SubmissionStatus.InReview,
    dagrun_pm_reviewedby: params.reviewerId,
    dagrun_pm_reviewedbyname: params.reviewerName,
    dagrun_pm_reviewedon: nowIso,
  });
  if (claim === 'conflict') return 'conflict';

  let data: Record<string, unknown> = {};
  try { data = fresh.dagrun_pm_submissiondatajson ? JSON.parse(fresh.dagrun_pm_submissiondatajson) : {}; } catch { /* empty */ }
  try {
    const created = await createTargetEntityFromSubmission(pca, params.form, params.fields, data, params.projectId);
    await dvUpdate(pca, 'dagrun_pm_submissions', fresh.dagrun_pm_submissionid, {
      dagrun_pm_status: SubmissionStatus.Complete,
      dagrun_pm_createdentitytype: created.entityType,
      dagrun_pm_createdentityid: created.entityId,
    });
    return 'approved';
  } catch (e) {
    await dvUpdate(pca, 'dagrun_pm_submissions', fresh.dagrun_pm_submissionid, {
      dagrun_pm_status: SubmissionStatus.Pending,
      dagrun_pm_reviewedby: null, dagrun_pm_reviewedbyname: null, dagrun_pm_reviewedon: null,
    }).catch(() => {});
    throw e;
  }
}

/** Reject a pending submission with a required reason. First-action-wins. */
export async function rejectSubmission(pca: IPublicClientApplication, params: {
  submission: Submission; reviewerId: string; reviewerName: string; notes: string;
}): Promise<ReviewOutcome> {
  const fresh = await getSubmission(pca, params.submission.dagrun_pm_submissionid);
  if (isSubmissionResolved(fresh) || fresh.dagrun_pm_status === SubmissionStatus.InReview) return 'conflict';
  const etag = fresh['@odata.etag'];
  if (!etag) return 'conflict';
  const outcome = await patchSubmissionIfMatch(pca, fresh.dagrun_pm_submissionid, etag, {
    dagrun_pm_status: SubmissionStatus.Rejected,
    dagrun_pm_reviewernotes: params.notes,
    dagrun_pm_reviewedby: params.reviewerId,
    dagrun_pm_reviewedbyname: params.reviewerName,
    dagrun_pm_reviewedon: new Date().toISOString(),
  });
  return outcome === 'applied' ? 'rejected' : 'conflict';
}

/**
 * Submit a filled-out form. When the form does NOT require review, the
 * target entity is created immediately and the submission is Complete.
 * Otherwise the submission sits Pending for an approver.
 */
export async function submitForm(pca: IPublicClientApplication, params: {
  form: FormDef; fields: FormField[]; projectId: string;
  data: Record<string, unknown>; submitterName?: string | null; submitterEmail?: string | null;
}): Promise<{ submissionId: string; created?: { entityType: string; entityId: string } }> {
  const submissionId = await createSubmission(pca, {
    formId: params.form.dagrun_pm_formid, projectId: params.projectId,
    data: params.data, submitterName: params.submitterName, submitterEmail: params.submitterEmail,
  });
  if (params.form.dagrun_pm_requiresreview === false) {
    const created = await createTargetEntityFromSubmission(pca, params.form, params.fields, params.data, params.projectId);
    await dvUpdate(pca, 'dagrun_pm_submissions', submissionId, {
      dagrun_pm_status: SubmissionStatus.Complete,
      dagrun_pm_createdentitytype: created.entityType,
      dagrun_pm_createdentityid: created.entityId,
    });
    return { submissionId, created };
  }
  return { submissionId };
}
